/**
 * Upgrade drawer — 480px right panel over a blurred scrim.
 *
 * Deliberately non-transactional CTA plumbing. The public/dashboard read
 * surface has no session auth yet, so it must not call a billing endpoint or
 * expose a checkout URL. Once authenticated sessions exist, this drawer can
 * call the protected server-side upgrade endpoint.
 */
import { useEffect } from 'react';
import { c, mono, radius, shadow } from '../theme/tokens';
import { LinkButton } from './primitives';
import type { UpgradeFocus } from '../types';

const PRO_ITEMS = [
  {
    name: 'Viewings booked into the diary',
    note: 'Book direct to your CRM diary, or capture the request and email it for the team to confirm. Your choice, per branch.',
  },
  {
    name: 'Property and lead context on every call',
    note: 'The assistant reads your live stock, so callers hear the right property, price and status.',
  },
  {
    name: 'Vendor qualification, scored',
    note: 'Up to five questions you write, weighted 1–10, so the hottest vendor leads sit at the top of the queue.',
  },
  {
    name: 'Conversion reporting',
    note: 'Enquiry to viewing, requests awaiting confirmation, and the properties and areas asked for most.',
  },
];

const SELECT_ITEMS = [
  {
    name: 'Direct viewing requests into your CRM',
    note: 'VoiceProp can submit a confirmed viewing request into your connected CRM, rather than only handing the message to the office.',
  },
  {
    name: 'Property conversations',
    note: 'See which listings callers ask about, the questions they raise and where demand is building.',
  },
  {
    name: 'New business opportunities',
    note: 'Surface the strongest vendor, landlord, valuation and instruction conversations without turning every call into a task.',
  },
  {
    name: 'Knowledge and team routing',
    note: 'Give the assistant approved branch knowledge and route named-person enquiries appropriately.',
  },
];

const SELECT_FOCUS: Record<UpgradeFocus, { heading: string; description: string }> = {
  'property-conversations': {
    heading: 'See what each property conversation is telling you',
    description: 'Select brings property-level conversation insight together, so your team can see the listings callers ask about and the questions they raise.',
  },
  'area-conversations': {
    heading: 'See where demand is building',
    description: 'Select turns area conversations into a clear view of the locations callers ask about, helping your team spot demand without inventing a new report.',
  },
  'viewings-requested': {
    heading: 'Keep every viewing request moving',
    description: 'Select records confirmed viewing requests in your connected CRM, giving the team a dependable handover rather than another message to interpret.',
  },
  'viewings-booked': {
    heading: 'Turn confirmed viewing requests into booked appointments',
    description: 'Select connects VoiceProp to your CRM diary, so confirmed requests can become bookable appointments with the right team process behind them.',
  },
};

export function UpgradeDrawer({ tier, focus, onClose }: { tier: 'pro' | 'select'; focus?: UpgradeFocus; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isSelect = tier === 'select';
  const items = isSelect ? SELECT_ITEMS : PRO_ITEMS;
  const focusCopy = isSelect && focus ? SELECT_FOCUS[focus] : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(11,18,32,0.55)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 40,
      }}
      role="dialog"
      aria-modal="true"
      aria-label={isSelect ? 'Select tier' : 'Pro tier'}
    >
      <button className="vp-reset" onClick={onClose} aria-label="Close" style={{ flex: 1, cursor: 'default' }} />

      <div
        style={{
          flex: '0 0 480px',
          background: c.surface2,
          height: '100%',
          overflowY: 'auto',
          padding: '40px 40px 48px 40px',
          display: 'flex',
          flexDirection: 'column',
          gap: 28,
          boxShadow: shadow.drawer,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <span
            style={{
              fontFamily: mono,
              fontSize: 9.5,
              letterSpacing: '0.14em',
              color: c.orange700,
              border: '1px solid rgba(198,111,0,0.35)',
              borderRadius: radius.pill,
              padding: '3px 9px 4px 9px',
            }}
          >
            {isSelect ? 'SELECT' : 'PRO'}
          </span>
          <button
            className="vp-reset vp-ghost"
            onClick={onClose}
            aria-label="Close"
            style={{ fontSize: 20, color: c.ink3, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2
            style={{
              margin: 0,
              fontSize: 32,
              lineHeight: 1.12,
              letterSpacing: '-0.015em',
              color: c.ink,
              textWrap: 'pretty',
            }}
          >
            {focusCopy?.heading ?? (isSelect ? 'Turn the right conversations into action' : 'Turn calls into viewings and vendor leads')}
          </h2>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: c.ink2 }}>
            {focusCopy?.description ?? (isSelect
              ? 'Select adds direct CRM viewing requests, property conversation insight and the highest-value new business opportunities.'
              : 'Everything you have now, plus the CRM connection that lets the assistant act rather than only listen. Nothing you have set up changes.')}
          </p>
        </div>

        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            background: c.line,
            border: `1px solid ${c.line}`,
            borderRadius: radius.list,
            overflow: 'hidden',
          }}
        >
          {items.map(u => (
            <div
              key={u.name}
              style={{
                flex: '0 0 auto',
                background: c.surface2,
                padding: '18px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 15.5, color: c.ink }}>{u.name}</span>
              <span style={{ fontSize: 13.5, lineHeight: 1.55, color: c.ink3 }}>{u.note}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div
            style={{
              textAlign: 'center',
              padding: 14,
              borderRadius: radius.button,
              background: c.lineSoft,
              border: `1px solid ${c.line}`,
              color: c.ink2,
              fontSize: 15,
            }}
          >
            {isSelect ? 'Select upgrade requires your account manager' : 'Pro upgrade available after secure sign-in'}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: c.ink3, textAlign: 'center' }}>
            Checkout is intentionally unavailable in this dashboard until authenticated account sessions are live. Your ModelProp account manager can arrange the upgrade.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LinkButton onClick={onClose} size={14} underline={false}>
              Not now
            </LinkButton>
          </div>
        </div>

        <div
          style={{
            fontFamily: mono,
            fontSize: 10.5,
            lineHeight: 1.7,
            color: c.ink3,
            borderTop: `1px solid ${c.lineSoft}`,
            paddingTop: 18,
          }}
        >
          {isSelect
            ? 'Secure upgrade endpoint: target tier + allowance + £199 one-off setup fee'
            : 'Secure upgrade endpoint: existing Stripe customer required · CRM connection required'}
        </div>
      </div>
    </div>
  );
}
